import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  TrafficLayer,
  DirectionsRenderer,
  InfoWindow,
  Circle,
} from '@react-google-maps/api';
import toast from 'react-hot-toast';
import {
  RefreshCw,
  Trash2,
  Car,
  User,
  MapPin,
  Clock,
  TrafficCone,
  Layers,
  Sparkles,
  Unlink,
  Zap,
  Maximize2,
  Filter,
} from 'lucide-react';
import { api } from '../lib/api';
import OsmRidesMap from './OsmRidesMap';

const ISRAEL_CENTER = { lat: 31.5, lng: 34.9 };
const POLL_MS = 30000;
const MAP_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

/** סגנון עדין — שומר על קריאות שכבת התנועה */
const MAP_STYLES: google.maps.MapTypeStyle[] = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'administrative', elementType: 'labels.text.fill', stylers: [{ color: '#5C6578' }] },
  { featureType: 'water', stylers: [{ color: '#D6E4EF' }] },
  { featureType: 'landscape', stylers: [{ color: '#F3F5F8' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#E8EDF3' }] },
];

type Eta = {
  distance_m: number;
  duration_s: number;
  duration_in_traffic_s: number | null;
  duration_display: string;
  distance_km: number;
  provider: 'google' | 'osrm' | 'none';
  has_traffic: boolean;
  delay_s: number | null;
};

type Ride = {
  id: string;
  driver_name: string;
  driver_phone: string;
  origin: string;
  origin_lat: number | null;
  origin_lng: number | null;
  direction: string;
  seats_total: number;
  seats_taken: number;
  status: string;
  notes: string | null;
  eta?: Eta | null;
};

type RideRequest = {
  id: string;
  passenger_name: string;
  passenger_phone: string;
  origin: string;
  origin_lat: number | null;
  origin_lng: number | null;
  direction?: string;
  seats_needed: number;
  status: string;
  ride_id: string | null;
  rides?: { driver_name: string; driver_phone: string } | null;
  eta?: Eta | null;
};

type MatchSuggestion = {
  ride_id: string;
  request_id: string;
  score: number;
  grade: 'excellent' | 'good' | 'fair' | 'weak';
  reasons: string[];
  warnings: string[];
  metrics: {
    seats_free: number;
    distance_km: number | null;
    detour_km: number | null;
    off_path_km?: number | null;
    detour_ratio?: number | null;
    eta_diff_min: number | null;
    wait_hours: number | null;
  };
  ride: {
    id: string;
    driver_name: string;
    driver_phone: string;
    origin: string;
    direction: string;
    seats_free: number;
  };
};

type EventInfo = {
  id: string;
  name: string;
  venue_name: string | null;
  venue_address: string | null;
  venue_lat: number | null;
  venue_lng: number | null;
  public_token: string | null;
};

type MapData = {
  event: EventInfo;
  rides: Ride[];
  requests: RideRequest[];
  routing?: { provider: string; has_traffic: boolean };
};

type Selection =
  | { kind: 'venue' }
  | { kind: 'ride'; id: string }
  | { kind: 'request'; id: string }
  | null;

type DriverNavRoute = {
  rideId: string;
  color: string;
  directions: google.maps.DirectionsResult;
  distanceText: string;
  durationText: string;
  durationTrafficText: string | null;
  steps: Array<{ instruction: string; distance: string; duration: string }>;
  waypointCount: number;
  summary: string;
};

const DRIVER_ROUTE_COLORS = [
  '#B05F7E',
  '#2A6B58',
  '#0F766E',
  '#A8895A',
  '#944E68',
  '#3D7A6A',
  '#8B6914',
  '#2563EB',
  '#7C3AED',
  '#B45309',
  '#0E7490',
  '#BE123C',
  '#4338CA',
  '#047857',
  '#C2410C',
];

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function formatNavDuration(seconds: number): string {
  const m = Math.max(1, Math.round(seconds / 60));
  if (m < 60) return `${m} דק׳`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h} שע׳ ${rem} דק׳` : `${h} שע׳`;
}

function pinSvg(label: string, bg: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="56" viewBox="0 0 44 56">
    <path d="M22 54s18-16.5 18-32A18 18 0 1 0 4 22c0 15.5 18 32 18 32z" fill="${bg}" stroke="#fff" stroke-width="2"/>
    <circle cx="22" cy="22" r="11" fill="#fff" fill-opacity=".18"/>
    <text x="22" y="27" text-anchor="middle" font-family="Heebo,Arial,sans-serif" font-size="13" font-weight="700" fill="#fff">${label}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

const ICONS = {
  venue: pinSvg('א', '#B05F7E'),
  ride: pinSvg('נ', '#2A6B58'),
  rideFull: pinSvg('נ', '#8B93A7'),
  request: pinSvg('מ', '#A8895A'),
  matched: pinSvg('✓', '#944E68'),
  /** נוסע של הנהג הנבחר */
  pickup: pinSvg('ס', '#C45C26'),
};

function EtaBadge({ eta }: { eta: Eta }) {
  const delayed = eta.has_traffic && (eta.delay_s ?? 0) >= 120;
  return (
    <span
      className={`chip ${delayed ? 'bg-brass-50 text-brass-600' : 'bg-sage-50 text-sage-700'}`}
      title={eta.has_traffic ? 'כולל תנועה חיה' : 'הערכה בסיסית'}
    >
      <Clock size={11} />
      {eta.duration_display}
      {eta.distance_km ? ` · ${eta.distance_km} ק״מ` : ''}
      {delayed ? ` · +${Math.round((eta.delay_s ?? 0) / 60)} דק׳` : ''}
    </span>
  );
}

export default function RidesLiveMap({
  eventId,
  onExit,
}: {
  eventId: string;
  onExit?: () => void;
}) {
  const [data, setData] = useState<MapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [showTraffic, setShowTraffic] = useState(true);
  const [routeMode, setRouteMode] = useState<'selected' | 'all'>('selected');
  const [showDrivers, setShowDrivers] = useState(true);
  const [showPending, setShowPending] = useState(true);
  const [showMatched, setShowMatched] = useState(false);
  const [listsOpen, setListsOpen] = useState(false);
  const [selection, setSelection] = useState<Selection>(null);
  const [routeResults, setRouteResults] = useState<DriverNavRoute[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [showNavPanel, setShowNavPanel] = useState(true);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const hasFittedRef = useRef(false);
  const routesFpRef = useRef('');
  const dataRef = useRef(data);
  dataRef.current = data;
  const [googleDenied, setGoogleDenied] = useState(!MAP_KEY);
  const [googleAuthSettled, setGoogleAuthSettled] = useState(!MAP_KEY);

  const matchingFp = useMemo(() => {
    if (!data) return '';
    const rides = data.rides
      .map((r) => `${r.id}:${r.status}:${r.origin_lat}:${r.origin_lng}`)
      .sort()
      .join('|');
    const matched = data.requests
      .filter((q) => q.status === 'matched')
      .map((q) => `${q.id}>${q.ride_id}:${q.origin_lat}:${q.origin_lng}`)
      .sort()
      .join('|');
    return `${rides}#${matched}`;
  }, [data]);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAP_KEY || '',
    language: 'he',
    region: 'IL',
  });

  useEffect(() => {
    const w = window as unknown as { gm_authFailure?: () => void };
    const prev = w.gm_authFailure;
    w.gm_authFailure = () => setGoogleDenied(true);
    return () => {
      w.gm_authFailure = prev;
    };
  }, []);

  useEffect(() => {
    if (loadError) setGoogleDenied(true);
  }, [loadError]);

  useEffect(() => {
    if (!MAP_KEY || googleDenied || loadError) {
      setGoogleAuthSettled(true);
      return;
    }
    if (!isLoaded) return;
    const t = window.setTimeout(() => setGoogleAuthSettled(true), 1200);
    return () => window.clearTimeout(t);
  }, [isLoaded, googleDenied, loadError]);

  const useOsm = googleDenied || !!loadError || !MAP_KEY;

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const r = await api.get<MapData>(`/events/${eventId}/rides-map`);
        setData(r.data);
        setLastUpdate(new Date());
      } catch {
        if (!silent) toast.error('שגיאה בטעינת המפה');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [eventId],
  );

  useEffect(() => {
    void load();
    timer.current = setInterval(() => void load(true), POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  const venue = data?.event;
  const venuePoint =
    venue?.venue_lat != null && venue?.venue_lng != null
      ? { lat: Number(venue.venue_lat), lng: Number(venue.venue_lng) }
      : null;

  const fitPoints = useMemo(() => {
    const pts: google.maps.LatLngLiteral[] = [];
    if (venuePoint) pts.push(venuePoint);
    data?.rides.forEach((r) => {
      if (showDrivers && r.origin_lat != null && r.origin_lng != null) {
        pts.push({ lat: Number(r.origin_lat), lng: Number(r.origin_lng) });
      }
    });
    data?.requests.forEach((q) => {
      if (q.origin_lat == null || q.origin_lng == null) return;
      if (q.status === 'pending' && showPending) {
        pts.push({ lat: Number(q.origin_lat), lng: Number(q.origin_lng) });
      }
      if (q.status === 'matched' && showMatched) {
        pts.push({ lat: Number(q.origin_lat), lng: Number(q.origin_lng) });
      }
    });
    return pts;
  }, [data, venuePoint, showDrivers, showPending, showMatched]);

  const openRides = useMemo(
    () => (data?.rides ?? []).filter((r) => r.status === 'open' && r.seats_taken < r.seats_total),
    [data],
  );
  const pendingRequests = useMemo(
    () => (data?.requests ?? []).filter((q) => q.status === 'pending'),
    [data],
  );

  const fitMap = useCallback((map: google.maps.Map, points: google.maps.LatLngLiteral[]) => {
    if (points.length === 0) {
      map.setCenter(ISRAEL_CENTER);
      map.setZoom(8);
      return;
    }
    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(12);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 72);
  }, []);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      if (!hasFittedRef.current) {
        fitMap(map, fitPoints);
        hasFittedRef.current = true;
      }
    },
    [fitMap, fitPoints],
  );

  // מסלולים — רק כשמשתנה מבנה השיבוץ / בחירה, לא בכל פולינג של ETA
  useEffect(() => {
    const live = dataRef.current;
    if (!isLoaded || useOsm || !venuePoint || !live) {
      setRouteResults([]);
      routesFpRef.current = '';
      return;
    }

    const ridesWithCoords = live.rides.filter(
      (r) => r.origin_lat != null && r.origin_lng != null && r.status !== 'cancelled',
    );

    const targetRides =
      routeMode === 'selected' && selection?.kind === 'ride'
        ? ridesWithCoords.filter((r) => r.id === selection.id)
        : routeMode === 'selected'
          ? []
          : ridesWithCoords;

    const fp = `${routeMode}:${selection?.kind === 'ride' ? selection.id : ''}:${targetRides
      .map((r) => {
        const pickups = live.requests
          .filter((q) => q.status === 'matched' && q.ride_id === r.id)
          .map((q) => q.id)
          .sort()
          .join(',');
        return `${r.id}:${pickups}`;
      })
      .join('|')}`;

    if (fp === routesFpRef.current) return;

    if (targetRides.length === 0) {
      routesFpRef.current = fp;
      setRouteResults([]);
      setRoutesLoading(false);
      return;
    }

    const service = new google.maps.DirectionsService();
    let cancelled = false;
    setRoutesLoading(true);

    void (async () => {
      const results: DriverNavRoute[] = [];
      const concurrency = 2;
      const snapshot = dataRef.current;
      if (!snapshot) return;

      async function buildOne(r: Ride, colorIdx: number): Promise<DriverNavRoute | null> {
        const pickups = snapshot!.requests.filter(
          (q) =>
            q.status === 'matched' &&
            q.ride_id === r.id &&
            q.origin_lat != null &&
            q.origin_lng != null,
        );
        const waypoints = pickups.slice(0, 8).map((q) => ({
          location: { lat: Number(q.origin_lat), lng: Number(q.origin_lng) },
          stopover: true,
        }));

        try {
          const res = await service.route({
            origin: { lat: Number(r.origin_lat), lng: Number(r.origin_lng) },
            destination: venuePoint!,
            waypoints,
            optimizeWaypoints: waypoints.length > 1,
            provideRouteAlternatives: routeMode === 'selected',
            travelMode: google.maps.TravelMode.DRIVING,
            drivingOptions: {
              departureTime: new Date(),
              trafficModel: google.maps.TrafficModel.BEST_GUESS,
            },
            unitSystem: google.maps.UnitSystem.METRIC,
            region: 'IL',
            language: 'he',
          });

          const route = res.routes[0];
          if (!route?.legs?.length) return null;

          let distanceM = 0;
          let durationS = 0;
          let durationTrafficS = 0;
          let hasTraffic = false;
          const steps: DriverNavRoute['steps'] = [];

          for (const leg of route.legs) {
            distanceM += leg.distance?.value ?? 0;
            durationS += leg.duration?.value ?? 0;
            const traf = (leg as google.maps.DirectionsLeg & {
              duration_in_traffic?: google.maps.Duration;
            }).duration_in_traffic;
            if (traf?.value != null) {
              durationTrafficS += traf.value;
              hasTraffic = true;
            } else {
              durationTrafficS += leg.duration?.value ?? 0;
            }
            for (const step of leg.steps ?? []) {
              steps.push({
                instruction: stripHtml(step.instructions || ''),
                distance: step.distance?.text || '',
                duration: step.duration?.text || '',
              });
            }
          }

          return {
            rideId: r.id,
            color: DRIVER_ROUTE_COLORS[colorIdx % DRIVER_ROUTE_COLORS.length],
            directions: res,
            distanceText: `${(distanceM / 1000).toFixed(1)} ק״מ`,
            durationText: formatNavDuration(durationS),
            durationTrafficText: hasTraffic ? formatNavDuration(durationTrafficS) : null,
            steps,
            waypointCount: waypoints.length,
            summary: route.summary || '',
          };
        } catch {
          return null;
        }
      }

      for (let i = 0; i < targetRides.length; i += concurrency) {
        if (cancelled) break;
        const chunk = targetRides.slice(i, i + concurrency);
        const built = await Promise.all(
          chunk.map((r) =>
            buildOne(
              r,
              Math.max(
                0,
                snapshot.rides.findIndex((x) => x.id === r.id),
              ),
            ),
          ),
        );
        for (const b of built) {
          if (b) results.push(b);
        }
        if (targetRides.length > 1) await new Promise((r) => setTimeout(r, 150));
      }

      if (!cancelled) {
        routesFpRef.current = fp;
        setRouteResults(results);
        setRoutesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, venuePoint, matchingFp, routeMode, selection, useOsm]);

  useEffect(() => {
    if (selection?.kind === 'ride') setShowNavPanel(true);
  }, [selection]);

  async function match(reqId: string, rideId: string, force = false) {
    if (!rideId) return;
    try {
      await api.post(`/events/${eventId}/ride-requests/${reqId}/match`, {
        ride_id: rideId,
        force,
      });
      toast.success('שובץ! נשלחה הודעת WhatsApp לנהג ולנוסע');
      void load(true);
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { error?: string; can_force?: boolean } } })
        ?.response?.data;
      // חריגת מרחק/עיקוף — בשיבוץ ידני מציעים לאשר בכל זאת
      if (data?.can_force && !force) {
        const ok = window.confirm(`${data.error}\n\nזהו שיבוץ ידני — לשבץ בכל זאת?`);
        if (ok) return match(reqId, rideId, true);
        return;
      }
      toast.error(data?.error || 'שגיאה בשיבוץ');
    }
  }

  async function unmatch(reqId: string) {
    try {
      await api.post(`/events/${eventId}/ride-requests/${reqId}/unmatch`);
      toast.success('השיבוץ בוטל — המושבים שוחררו');
      void load(true);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'שגיאה בביטול שיבוץ');
    }
  }

  async function runAutoMatch(dryRun = false) {
    try {
      const r = await api.post<{
        dry_run: boolean;
        planned: number | unknown[];
        matched: unknown[];
        min_score: number;
      }>(`/events/${eventId}/matching/auto-match`, { min_score: 70, dry_run: dryRun });
      if (dryRun) {
        const n = Array.isArray(r.data.planned) ? r.data.planned.length : Number(r.data.planned) || 0;
        toast.success(n ? `תצוגה מקדימה: ${n} שיבוצים מוצעים` : 'אין שיבוצים מעל הסף (70)');
        return;
      }
      const n = r.data.matched?.length ?? 0;
      toast.success(n ? `שובצו אוטומטית ${n} נוסעים` : 'לא בוצעו שיבוצים חדשים');
      void load(true);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'שגיאה בשיבוץ אוטומטי');
    }
  }

  async function runReoptimize() {
    try {
      toast.loading('מתקן עיקופים ומשבץ מחדש…', { id: 'reopt' });
      const r = await api.post<{
        released_count: number;
        rematched_count: number;
        bad_count: number;
      }>(`/events/${eventId}/matching/reoptimize`, { min_score: 70, dry_run: false });
      toast.success(
        `שוחררו ${r.data.released_count} עיקופים · שובצו מחדש ${r.data.rematched_count}`,
        { id: 'reopt' },
      );
      void load(true);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'שגיאה בתיקון שיבוצים', { id: 'reopt' });
    }
  }

  async function removeRide(id: string) {
    await api.delete(`/events/${eventId}/rides/${id}`);
    void load(true);
  }
  async function removeRequest(id: string) {
    await api.delete(`/events/${eventId}/ride-requests/${id}`);
    void load(true);
  }

  const selectedRide =
    selection?.kind === 'ride' ? data?.rides.find((r) => r.id === selection.id) : undefined;
  const selectedReq =
    selection?.kind === 'request' ? data?.requests.find((q) => q.id === selection.id) : undefined;
  const selectedNav = selectedRide
    ? routeResults.find((r) => r.rideId === selectedRide.id)
    : undefined;

  /** כל הטרמפים (נוסעים משובצים) של הנהג שנבחר */
  const selectedRidePickups = useMemo(() => {
    if (!selectedRide || !data) return [];
    return data.requests.filter(
      (q) =>
        q.status === 'matched' &&
        q.ride_id === selectedRide.id &&
        q.origin_lat != null &&
        q.origin_lng != null,
    );
  }, [selectedRide, data]);

  const selectedPickupIds = useMemo(
    () => new Set(selectedRidePickups.map((q) => q.id)),
    [selectedRidePickups],
  );

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const shell = (
    <div
      className="bg-[#0c1017] flex flex-col"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100dvh',
        zIndex: 9999,
      }}
      dir="rtl"
    >
      {/* Top bar */}
      <div className="shrink-0 flex flex-wrap items-center gap-2 px-3 py-2.5 bg-[#3A3340]/95 backdrop-blur border-b border-white/10 text-white">
        <button
          type="button"
          onClick={() => onExit?.()}
          className="btn-ghost !py-1.5 !px-2.5 text-xs !text-white/80 hover:!text-white !bg-white/5"
        >
          ← חזרה
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate">{venue?.name || 'מפת טרמפים'}</div>
          <div className="text-[11px] text-white/50">
            {pendingRequests.length} ממתינים · {data?.rides.length ?? 0} נהגים
            {lastUpdate ? ` · ${lastUpdate.toLocaleTimeString('he-IL')}` : ''}
            <span className="mx-1.5">·</span>
            <span className="text-brass-400">
              {useOsm
                ? 'מפה: OpenStreetMap · Google Maps חסום בפרויקט המפתח'
                : 'עיקוף איסוף עד 5 דק׳ ממסלול Google · מיקום = מוצא (לא GPS חי)'}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setShowDrivers((v) => !v)}
            className={`btn !py-1.5 !px-2.5 text-xs ${showDrivers ? 'btn-primary' : 'btn-ghost !text-white/70 !bg-white/5'}`}
          >
            נהגים
          </button>
          <button
            type="button"
            onClick={() => setShowPending((v) => !v)}
            className={`btn !py-1.5 !px-2.5 text-xs ${showPending ? 'btn-secondary' : 'btn-ghost !text-white/70 !bg-white/5'}`}
          >
            ממתינים
          </button>
          <button
            type="button"
            onClick={() => setShowMatched((v) => !v)}
            className={`btn !py-1.5 !px-2.5 text-xs ${showMatched ? 'btn-secondary' : 'btn-ghost !text-white/70 !bg-white/5'}`}
          >
            משובצים
          </button>
          <button
            type="button"
            onClick={() => setRouteMode((m) => (m === 'selected' ? 'all' : 'selected'))}
            className={`btn !py-1.5 !px-2.5 text-xs ${routeMode === 'all' ? 'btn-primary' : 'btn-ghost !text-white/70 !bg-white/5'}`}
            title="ברירת מחדל: מסלול רק לנהג שנבחר"
          >
            <Layers size={13} />
            {routeMode === 'selected' ? 'מסלול נבחר' : 'כל המסלולים'}
          </button>
          <button
            type="button"
            onClick={() => setShowTraffic((v) => !v)}
            className={`btn !py-1.5 !px-2.5 text-xs ${showTraffic ? 'btn-primary' : 'btn-ghost !text-white/70 !bg-white/5'}`}
          >
            <TrafficCone size={13} />
            עומסים
          </button>
          <button
            type="button"
            onClick={() => {
              if (mapRef.current) {
                hasFittedRef.current = true;
                fitMap(mapRef.current, fitPoints);
              }
            }}
            className="btn-ghost !py-1.5 !px-2.5 text-xs !text-white/80 !bg-white/5"
          >
            <Maximize2 size={13} />
            התאם
          </button>
          <button
            type="button"
            onClick={() => void load()}
            className="btn-ghost !py-1.5 !px-2.5 text-xs !text-white/80 !bg-white/5"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={() => setListsOpen((v) => !v)}
            className={`btn !py-1.5 !px-2.5 text-xs ${listsOpen ? 'btn-primary' : 'btn-ghost !text-white/70 !bg-white/5'}`}
          >
            <Filter size={13} />
            רשימות
          </button>
          <button
            type="button"
            onClick={() => void runReoptimize()}
            className="btn-secondary !py-1.5 !px-2.5 text-xs"
            title="משחרר שיבוצים שמאריכים מעל 5 דק׳ ממסלול Google ומשבץ מחדש"
          >
            <Sparkles size={13} />
            תקן עיקופים
          </button>
          <button
            type="button"
            onClick={() => void runAutoMatch(false)}
            disabled={pendingRequests.length === 0 || openRides.length === 0}
            className="btn-primary !py-1.5 !px-2.5 text-xs"
          >
            <Zap size={13} />
            שיבוץ
          </button>
        </div>
      </div>

      {/* Map full screen */}
      <div className="relative flex-1 min-h-0">
        {useOsm ? (
          <OsmRidesMap
            icons={ICONS}
            venuePoint={venuePoint}
            venueName={venue?.venue_name || venue?.name || 'אולם'}
            venueAddress={venue?.venue_address ?? null}
            rides={data?.rides ?? []}
            requests={data?.requests ?? []}
            showDrivers={showDrivers}
            showPending={showPending}
            showMatched={showMatched}
            selection={selection}
            selectedPickupIds={selectedPickupIds}
            selectedRidePickups={selectedRidePickups}
            onSelect={setSelection}
          />
        ) : !isLoaded || !googleAuthSettled ? (
          <div className="absolute inset-0 grid place-items-center bg-[#3A3340]">
            <p className="text-sm text-white/50">טוען Google Maps…</p>
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={venuePoint ?? ISRAEL_CENTER}
            zoom={venuePoint ? 10 : 8}
            onLoad={onMapLoad}
            onClick={() => setSelection(null)}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              clickableIcons: false,
              styles: MAP_STYLES,
              gestureHandling: 'greedy',
              minZoom: 7,
              maxZoom: 16,
            }}
          >
            {showTraffic && <TrafficLayer />}

            {routeResults.map((nav) => {
                const isSelected = selection?.kind === 'ride' && selection.id === nav.rideId;
                const dimmed = selection?.kind === 'ride' && !isSelected;
                const altCount = isSelected ? Math.min(nav.directions.routes?.length ?? 1, 3) : 1;
                return Array.from({ length: altCount }, (_, routeIdx) => {
                  const isAlt = routeIdx > 0;
                  return (
                    <DirectionsRenderer
                      key={`${nav.rideId}-${routeIdx}`}
                      directions={nav.directions}
                      routeIndex={routeIdx}
                      options={{
                        suppressMarkers: true,
                        preserveViewport: true,
                        polylineOptions: {
                          strokeColor: isAlt ? '#8B93A7' : nav.color,
                          strokeOpacity: isAlt ? 0.4 : dimmed ? 0.2 : isSelected || routeMode === 'selected' ? 0.95 : 0.65,
                          strokeWeight: isAlt ? 4 : isSelected || routeMode === 'selected' ? 7 : 4,
                          zIndex: isSelected ? (isAlt ? 70 : 90) : 50,
                        },
                      }}
                    />
                  );
                });
              })}

            {venuePoint && (
              <Marker
                position={venuePoint}
                icon={{
                  url: ICONS.venue,
                  scaledSize: new google.maps.Size(40, 52),
                  anchor: new google.maps.Point(20, 52),
                }}
                zIndex={1000}
                onClick={() => setSelection({ kind: 'venue' })}
              />
            )}

            {showDrivers &&
              data?.rides.map((r) => {
                if (r.origin_lat == null || r.origin_lng == null) return null;
                const isSel = selection?.kind === 'ride' && selection.id === r.id;
                const dimOther = selection?.kind === 'ride' && !isSel;
                return (
                  <Marker
                    key={r.id}
                    position={{ lat: Number(r.origin_lat), lng: Number(r.origin_lng) }}
                    icon={{
                      url: r.seats_taken >= r.seats_total ? ICONS.rideFull : ICONS.ride,
                      scaledSize: new google.maps.Size(isSel ? 44 : 36, isSel ? 56 : 46),
                      anchor: new google.maps.Point(isSel ? 22 : 18, isSel ? 56 : 46),
                    }}
                    opacity={dimOther ? 0.35 : 1}
                    zIndex={isSel ? 900 : 200}
                    onClick={() => setSelection({ kind: 'ride', id: r.id })}
                  />
                );
              })}

            {data?.requests.map((q) => {
              if (q.origin_lat == null || q.origin_lng == null) return null;
              const isPickupOfSelected = selectedPickupIds.has(q.id);
              if (q.status === 'pending' && !showPending) return null;
              // משובצים: רק אם שכבה פתוחה, או שהם של הנהג הנבחר
              if (q.status === 'matched' && !showMatched && !isPickupOfSelected) return null;
              if (q.status !== 'pending' && q.status !== 'matched') return null;

              const dimOthers =
                selection?.kind === 'ride' && !isPickupOfSelected && q.status !== 'pending';

              return (
                <Marker
                  key={q.id}
                  position={{ lat: Number(q.origin_lat), lng: Number(q.origin_lng) }}
                  icon={{
                    url: isPickupOfSelected
                      ? ICONS.pickup
                      : q.status === 'matched'
                        ? ICONS.matched
                        : ICONS.request,
                    scaledSize: new google.maps.Size(
                      isPickupOfSelected ? 44 : 32,
                      isPickupOfSelected ? 56 : 42,
                    ),
                    anchor: new google.maps.Point(
                      isPickupOfSelected ? 22 : 16,
                      isPickupOfSelected ? 56 : 42,
                    ),
                  }}
                  opacity={dimOthers ? 0.25 : 1}
                  zIndex={isPickupOfSelected ? 850 : 100}
                  onClick={() => setSelection({ kind: 'request', id: q.id })}
                />
              );
            })}

            {/* טבעות הדגשה סביב איסופי הנהג הנבחר */}
            {selectedRidePickups.map((q) => (
              <Circle
                key={`ring-${q.id}`}
                center={{ lat: Number(q.origin_lat), lng: Number(q.origin_lng) }}
                radius={450}
                options={{
                  strokeColor: '#C45C26',
                  strokeOpacity: 0.95,
                  strokeWeight: 2,
                  fillColor: '#C45C26',
                  fillOpacity: 0.18,
                  zIndex: 80,
                  clickable: false,
                }}
              />
            ))}

            {selection?.kind === 'venue' && venuePoint && (
              <InfoWindow position={venuePoint} onCloseClick={() => setSelection(null)}>
                <div dir="rtl" className="min-w-[180px] p-1 font-[Heebo,sans-serif]">
                  <div className="font-bold text-[#3A3340] text-[15px]">
                    {venue?.venue_name || venue?.name}
                  </div>
                  {venue?.venue_address && (
                    <div className="text-[12px] text-[#5C6578] mt-1">{venue.venue_address}</div>
                  )}
                </div>
              </InfoWindow>
            )}

            {selectedRide && selectedRide.origin_lat != null && selectedRide.origin_lng != null && (
              <InfoWindow
                position={{
                  lat: Number(selectedRide.origin_lat),
                  lng: Number(selectedRide.origin_lng),
                }}
                onCloseClick={() => setSelection(null)}
              >
                <div dir="rtl" className="min-w-[200px] p-1 font-[Heebo,sans-serif]">
                  <div className="text-[11px] font-semibold text-[#2A6B58] uppercase mb-1">נהג</div>
                  <div className="font-bold text-[#3A3340] text-[15px]">{selectedRide.driver_name}</div>
                  <div className="text-[12px] text-[#5C6578] mt-1">
                    {selectedRide.origin} · מקומות {selectedRide.seats_taken}/{selectedRide.seats_total}
                  </div>
                  {selectedRidePickups.length > 0 ? (
                    <div className="mt-2 border-t border-[#E8EDF3] pt-2 space-y-1">
                      <div className="text-[11px] font-semibold text-[#C45C26]">
                        אוסף {selectedRidePickups.length} נוסעים
                      </div>
                      {selectedRidePickups.map((p) => (
                        <div key={p.id} className="text-[12px] text-[#3A3340]">
                          <span className="font-semibold">{p.passenger_name}</span>
                          <span className="text-[#5C6578]"> · {p.origin}</span>
                          {p.seats_needed > 1 ? (
                            <span className="text-[#8B93A7]"> (×{p.seats_needed})</span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 text-[11px] text-[#8B93A7]">אין נוסעים משובצים עדיין</div>
                  )}
                  {selectedNav && (
                    <div className="mt-2 text-[12px] text-[#944E68] font-semibold">
                      {selectedNav.durationTrafficText || selectedNav.durationText} · {selectedNav.distanceText}
                      {selectedNav.waypointCount > 0 ? ` · ${selectedNav.waypointCount} איסופים` : ''}
                    </div>
                  )}
                  <div className="mt-1 text-[11px] text-[#8B93A7]">
                    סיכות כתומות = נוסעים שלו על המפה
                  </div>
                </div>
              </InfoWindow>
            )}

            {selectedReq && selectedReq.origin_lat != null && selectedReq.origin_lng != null && (
              <InfoWindow
                position={{
                  lat: Number(selectedReq.origin_lat),
                  lng: Number(selectedReq.origin_lng),
                }}
                onCloseClick={() => setSelection(null)}
              >
                <div dir="rtl" className="min-w-[180px] p-1 font-[Heebo,sans-serif]">
                  <div className="font-bold text-[#3A3340] text-[15px]">{selectedReq.passenger_name}</div>
                  <div className="text-[12px] text-[#5C6578] mt-1">{selectedReq.origin}</div>
                  {selectedReq.status === 'matched' && selectedReq.rides && (
                    <div className="mt-1 text-[12px] text-[#944E68] font-semibold">
                      שובץ ל-{selectedReq.rides.driver_name}
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}

        <div className="absolute bottom-3 right-3 z-[1] rounded-xl bg-white/95 shadow-float px-3 py-2.5 text-[11px] text-ink-mute space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-sage-600" /> אולם
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-sage-500" /> נהג (מוצא)
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-brass-500" /> ממתין
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#C45C26]" /> איסוף של הנהג
          </div>
          <div className="pt-1 border-t border-line text-[10px] text-ink-faint">
            {routesLoading
              ? 'מחשב מסלול…'
              : routeMode === 'selected'
                ? 'לחצו על נהג למסלול ניווט'
                : `${routeResults.length} מסלולים`}
          </div>
        </div>

        {selectedNav && selectedRide && showNavPanel && (
          <div className="absolute top-3 left-3 z-[2] w-[min(100%-1.5rem,340px)] max-h-[min(70%,520px)] overflow-hidden rounded-xl bg-white/95 shadow-float flex flex-col">
            <div className="px-3 py-2.5 border-b border-line flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold tracking-wide text-sage-600 uppercase">
                  ניווט מתוכנן
                </div>
                <div className="font-semibold text-ink text-sm truncate">{selectedRide.driver_name}</div>
                <div className="text-[11px] text-ink-faint truncate">
                  {selectedRide.origin} → {venue?.venue_name || 'אולם'}
                  {selectedNav.waypointCount > 0 ? ` · ${selectedNav.waypointCount} איסופים` : ''}
                </div>
              </div>
              <button type="button" className="text-ink-faint text-xs" onClick={() => setShowNavPanel(false)}>
                ✕
              </button>
            </div>
            <div className="px-3 py-2 flex flex-wrap gap-1.5 text-[11px] border-b border-line">
              <span className="chip bg-sage-50 text-sage-700">
                <Clock size={11} />
                {selectedNav.durationTrafficText || selectedNav.durationText}
              </span>
              <span className="chip bg-paper-mist text-ink-mute">{selectedNav.distanceText}</span>
            </div>
            <div className="overflow-auto flex-1 px-1 py-1">
              {selectedNav.steps.slice(0, 50).map((step, i) => (
                <div key={i} className="flex gap-2 px-2 py-1.5 text-[11px] border-b border-line/60">
                  <span className="text-ink-faint w-5 shrink-0">{i + 1}</span>
                  <div className="min-w-0">
                    <div className="text-ink leading-snug">{step.instruction}</div>
                    <div className="text-ink-faint mt-0.5">
                      {step.distance}
                      {step.duration ? ` · ${step.duration}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lists drawer */}
      {listsOpen && (
        <div className="shrink-0 max-h-[40vh] overflow-auto border-t border-white/10 bg-paper p-3 grid md:grid-cols-2 gap-3">
          <RidesPanel
            eventId={eventId}
            rides={data?.rides ?? []}
            onChange={() => void load(true)}
            onDelete={removeRide}
            onFocus={(id) => setSelection({ kind: 'ride', id })}
          />
          <RequestsPanel
            eventId={eventId}
            requests={data?.requests ?? []}
            openRides={openRides}
            pendingCount={pendingRequests.length}
            onChange={() => void load(true)}
            onMatch={match}
            onUnmatch={unmatch}
            onDelete={removeRequest}
            onFocus={(id) => setSelection({ kind: 'request', id })}
          />
        </div>
      )}
    </div>
  );

  return createPortal(shell, document.body);
}

function RidesPanel({
  eventId,
  rides,
  onChange,
  onDelete,
  onFocus,
}: {
  eventId: string;
  rides: Ride[];
  onChange: () => void;
  onDelete: (id: string) => void;
  onFocus: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [origin, setOrigin] = useState('');
  const [seats, setSeats] = useState('3');
  const [busy, setBusy] = useState(false);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !origin.trim()) return;
    setBusy(true);
    try {
      await api.post(`/events/${eventId}/rides`, {
        driver_name: name.trim(),
        driver_phone: phone.trim(),
        origin: origin.trim(),
        seats_total: parseInt(seats, 10) || 1,
      });
      setName('');
      setPhone('');
      setOrigin('');
      setSeats('3');
      onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="surface-quiet p-4 space-y-3">
      <h3 className="font-semibold text-ink flex items-center gap-2 text-sm">
        <Car size={16} className="text-sage-600" /> נהגים ({rides.length})
      </h3>
      <form onSubmit={add} className="grid grid-cols-2 gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="שם נהג" className="field !py-2 text-sm" />
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="טלפון" className="field !py-2 text-sm" dir="ltr" />
        <input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="עיר/כתובת מוצא" className="field !py-2 text-sm" />
        <div className="flex gap-2">
          <input value={seats} onChange={(e) => setSeats(e.target.value)} placeholder="מקומות" className="field !py-2 text-sm w-20" />
          <button type="submit" disabled={busy} className="btn-primary flex-1 !py-2 text-sm">
            הוסף
          </button>
        </div>
      </form>
      <div className="divide-y divide-line max-h-72 overflow-auto">
        {rides.length === 0 ? (
          <div className="py-6 text-center text-ink-faint text-sm">אין נסיעות</div>
        ) : (
          rides.map((r) => (
            <button
              type="button"
              key={r.id}
              onClick={() => onFocus(r.id)}
              className="w-full py-2.5 flex items-center justify-between gap-2 text-right hover:bg-paper/70 transition-colors cursor-pointer px-1 rounded-lg"
            >
              <div className="min-w-0">
                <div className="font-semibold text-ink text-sm truncate">{r.driver_name}</div>
                <div className="text-[11px] text-ink-faint flex items-center gap-1 truncate">
                  <MapPin size={11} /> {r.origin} · <span dir="ltr">{r.driver_phone}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.eta && <EtaBadge eta={r.eta} />}
                <span
                  className={`text-xs font-bold ${r.seats_taken >= r.seats_total ? 'text-ink-faint' : 'text-sage-600'}`}
                >
                  {r.seats_taken}/{r.seats_total}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(r.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      onDelete(r.id);
                    }
                  }}
                  className="p-1 text-ink-faint hover:text-red-500 cursor-pointer"
                  aria-label="מחק"
                >
                  <Trash2 size={14} />
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function gradeChip(grade: MatchSuggestion['grade'], score: number) {
  const styles: Record<MatchSuggestion['grade'], string> = {
    excellent: 'bg-sage-50 text-sage-700',
    good: 'bg-sage-50/70 text-sage-600',
    fair: 'bg-brass-50 text-brass-600',
    weak: 'bg-paper-mist text-ink-faint',
  };
  const labels: Record<MatchSuggestion['grade'], string> = {
    excellent: 'מעולה',
    good: 'טוב',
    fair: 'סביר',
    weak: 'חלש',
  };
  return (
    <span className={`chip ${styles[grade]}`}>
      {labels[grade]} · {score}
    </span>
  );
}

function RequestsPanel({
  eventId,
  requests,
  openRides,
  pendingCount,
  onChange,
  onMatch,
  onUnmatch,
  onDelete,
  onFocus,
}: {
  eventId: string;
  requests: RideRequest[];
  openRides: Ride[];
  pendingCount: number;
  onChange: () => void;
  onMatch: (reqId: string, rideId: string) => void;
  onUnmatch: (reqId: string) => void;
  onDelete: (id: string) => void;
  onFocus: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [origin, setOrigin] = useState('');
  const [seats, setSeats] = useState('1');
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState<Record<string, string>>({});
  const [suggestions, setSuggestions] = useState<Record<string, MatchSuggestion[]>>({});
  const [loadingSug, setLoadingSug] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<string | null>(null);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !origin.trim()) return;
    setBusy(true);
    try {
      await api.post(`/events/${eventId}/ride-requests`, {
        passenger_name: name.trim(),
        passenger_phone: phone.trim(),
        origin: origin.trim(),
        seats_needed: parseInt(seats, 10) || 1,
      });
      setName('');
      setPhone('');
      setOrigin('');
      setSeats('1');
      onChange();
    } finally {
      setBusy(false);
    }
  }

  async function loadSuggestions(reqId: string) {
    setLoadingSug((s) => ({ ...s, [reqId]: true }));
    try {
      const r = await api.get<{ suggestions: MatchSuggestion[] }>(
        `/events/${eventId}/ride-requests/${reqId}/suggestions?limit=5`,
      );
      setSuggestions((s) => ({ ...s, [reqId]: r.data.suggestions ?? [] }));
      const best = r.data.suggestions?.[0];
      if (best) setSel((s) => ({ ...s, [reqId]: best.ride_id }));
    } catch {
      toast.error('לא ניתן לטעון הצעות שיבוץ');
    } finally {
      setLoadingSug((s) => ({ ...s, [reqId]: false }));
    }
  }

  function toggleExpand(reqId: string) {
    if (expanded === reqId) {
      setExpanded(null);
      return;
    }
    setExpanded(reqId);
    if (!suggestions[reqId]) void loadSuggestions(reqId);
  }

  return (
    <div className="surface-quiet p-4 space-y-3">
      <h3 className="font-semibold text-ink flex items-center gap-2 text-sm">
        <User size={16} className="text-brass-500" /> מבקשי טרמפ ({pendingCount} ממתינים)
      </h3>
      <form onSubmit={add} className="grid grid-cols-2 gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="שם נוסע" className="field !py-2 text-sm" />
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="טלפון" className="field !py-2 text-sm" dir="ltr" />
        <input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="עיר/כתובת מוצא" className="field !py-2 text-sm" />
        <div className="flex gap-2">
          <input value={seats} onChange={(e) => setSeats(e.target.value)} placeholder="כמות" className="field !py-2 text-sm w-20" />
          <button type="submit" disabled={busy} className="btn-secondary flex-1 !py-2 text-sm">
            הוסף
          </button>
        </div>
      </form>
      <div className="divide-y divide-line max-h-[28rem] overflow-auto">
        {requests.length === 0 ? (
          <div className="py-6 text-center text-ink-faint text-sm">אין בקשות</div>
        ) : (
          requests.map((q) => (
            <div key={q.id} className="py-2.5 space-y-1.5">
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  onFocus(q.id);
                  if (q.status === 'pending') toggleExpand(q.id);
                }}
                onKeyDown={(e) => e.key === 'Enter' && onFocus(q.id)}
                className="flex items-center justify-between gap-2 cursor-pointer hover:bg-paper/70 rounded-lg px-1 transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-ink text-sm">
                    {q.passenger_name}{' '}
                    {q.seats_needed > 1 && (
                      <span className="text-[11px] text-ink-faint">(×{q.seats_needed})</span>
                    )}
                  </div>
                  <div className="text-[11px] text-ink-faint flex items-center gap-1 truncate flex-wrap">
                    <MapPin size={11} /> {q.origin} · <span dir="ltr">{q.passenger_phone}</span>
                    {q.eta && <EtaBadge eta={q.eta} />}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(q.id);
                  }}
                  className="p-1 text-ink-faint hover:text-red-500 cursor-pointer"
                  aria-label="מחק"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {q.status === 'matched' ? (
                <div className="flex items-center justify-between gap-2 px-1">
                  <div className="text-[12px] text-sage-600 font-semibold">
                    שובץ {q.rides ? `ל-${q.rides.driver_name}` : ''}
                  </div>
                  <button
                    type="button"
                    onClick={() => onUnmatch(q.id)}
                    className="btn-ghost !py-1 !px-2 text-[11px] text-ink-mute"
                  >
                    <Unlink size={12} />
                    בטל שיבוץ
                  </button>
                </div>
              ) : (
                <div className="space-y-2 px-1">
                  {expanded === q.id && (
                    <div className="rounded-xl bg-paper/80 border border-line p-2 space-y-1.5">
                      <div className="flex items-center justify-between text-[11px] text-ink-faint">
                        <span className="inline-flex items-center gap-1 font-semibold text-ink-mute">
                          <Sparkles size={11} /> הצעות מדורגות
                        </span>
                        {loadingSug[q.id] && <span>מחשב…</span>}
                      </div>
                      {(suggestions[q.id] ?? []).length === 0 && !loadingSug[q.id] ? (
                        <div className="text-[11px] text-ink-faint py-1">אין נהגים תואמים כרגע</div>
                      ) : (
                        (suggestions[q.id] ?? []).map((s) => (
                          <button
                            type="button"
                            key={s.ride_id}
                            onClick={() => setSel((prev) => ({ ...prev, [q.id]: s.ride_id }))}
                            className={`w-full text-right rounded-lg px-2 py-1.5 transition-colors ${
                              sel[q.id] === s.ride_id
                                ? 'bg-sage-50 ring-1 ring-sage-200'
                                : 'hover:bg-paper'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-[12px] font-semibold text-ink truncate">
                                  {s.ride.driver_name} · {s.ride.origin}
                                </div>
                                <div className="text-[10px] text-ink-faint truncate">
                                  {s.reasons[0] || 'התאמה'}
                                  {s.metrics.distance_km != null
                                    ? ` · ${s.metrics.distance_km} ק״מ`
                                    : ''}
                                  {s.metrics.detour_km != null
                                    ? ` · עיקוף ${s.metrics.detour_km} ק״מ`
                                    : ''}
                                  {s.metrics.off_path_km != null
                                    ? ` · מסדרון ${s.metrics.off_path_km}`
                                    : ''}
                                </div>
                              </div>
                              {gradeChip(s.grade, s.score)}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  {openRides.length > 0 ? (
                    <div className="flex gap-2">
                      <select
                        value={sel[q.id] ?? ''}
                        onChange={(e) => setSel((s) => ({ ...s, [q.id]: e.target.value }))}
                        onFocus={() => {
                          if (!suggestions[q.id]) void loadSuggestions(q.id);
                        }}
                        className="field !py-1.5 flex-1 text-xs"
                      >
                        <option value="">בחר נהג…</option>
                        {(suggestions[q.id]?.length
                          ? suggestions[q.id]
                          : openRides
                              .filter((r) => r.seats_total - r.seats_taken >= q.seats_needed)
                              .map(
                                (r) =>
                                  ({
                                    ride_id: r.id,
                                    score: 0,
                                    grade: 'fair' as const,
                                    ride: {
                                      driver_name: r.driver_name,
                                      origin: r.origin,
                                      seats_free: r.seats_total - r.seats_taken,
                                    },
                                  }) as Partial<MatchSuggestion> & { ride_id: string },
                              )
                        ).map((s) => (
                          <option key={s.ride_id} value={s.ride_id}>
                            {s.ride?.driver_name} · {s.ride?.origin}
                            {typeof s.score === 'number' && s.score > 0 ? ` · ציון ${s.score}` : ''}
                            {s.ride?.seats_free != null ? ` (${s.ride.seats_free} פנויים)` : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => onMatch(q.id, sel[q.id] ?? '')}
                        disabled={!sel[q.id]}
                        className="btn-primary !py-1.5 !px-3 text-xs"
                      >
                        שבץ
                      </button>
                    </div>
                  ) : (
                    <div className="text-[11px] text-ink-faint">אין נהגים פנויים מתאימים</div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
